import axios from 'axios';
import API_BASE from '../../config';
import { universeLabel } from '../../components/terminal/useUniverse';

/**
 * KAN-75 — data plumbing for the /terminal namespace. Thin wrappers over
 * existing endpoints; every call degrades to a thrown error the caller
 * renders as an empty-state (no token / BE down must never blank the page).
 */

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Sector strength for one lookback window. days: 1|10|21|63|126|252. */
export async function fetchSectors(days, index) {
  const { data } = await axios.get(`${API_BASE}/pulse/market-regime/sectors`, {
    params: { days, type: 'stock', ...(index ? { index } : {}) },
    headers: authHeaders(),
  });
  return data?.data?.sectors || [];
}

/**
 * Latest level and true 1-day change for an index (KAN-97).
 *
 * Use this rather than /stocks/stock/<symbol> for index quotes: that endpoint
 * returns bars without `prev_close`, so callers were deriving the day move as
 * (close - open) / open — intraday drift, which can differ in SIGN from the
 * day change a trader means. This endpoint computes it against the previous
 * close and resolves stale instruments to their refreshed series.
 */
export async function fetchIndexQuote(indexId) {
  const { data } = await axios.get(`${API_BASE}/analytics/index-quote`, {
    params: { index: indexId },
    headers: authHeaders(),
  });
  if (!data?.success) throw new Error(data?.error || 'index quote unavailable');
  return data;
}

/**
 * Market-regime reads (breadth / volatility / ma-health / regime).
 * breadth accepts optional `days` (defaults to 1, same as sectors).
 */
export async function fetchRegime(part, index, { days } = {}) {
  const params = { type: 'stock', ...(index ? { index } : {}) };
  if (part === 'breadth' && days != null) {
    params.days = days;
  }
  const { data } = await axios.get(`${API_BASE}/pulse/market-regime/${part}`, {
    params,
    headers: authHeaders(),
  });
  return data?.data ?? data;
}

/**
 * Open triggers (+ per-setup counts). Pass symbols to scope the SQL to exactly
 * those (sector drill-down).
 *
 * NOT grid-independent, despite what this said before. "Active" means the
 * trigger has not yet hit TP or SL *at the requested exits*, so widening the
 * grid leaves more triggers open: measured at days=5 over nifty_100, active
 * triggers run 298 (TP 0.5%/1:1) to 797 (TP 5%/4:1), and the setup count 56
 * to 66. Omitting tp/ratio does not mean "no grid" — the server falls back to
 * tp_level=1, sl_ratio=2 (_DEFAULT_TP / _DEFAULT_SL_RATIO in
 * blueprints/analytics/routes.py), so a caller that leaves them off silently
 * shows counts for exits the user never picked.
 */
export async function fetchActive({ days = 5, limit = 120, symbols, anchor, index, tp, ratio } = {}) {
  const params = { days, limit };
  if (index) params.index = index;
  if (tp != null) params.tp_level = tp;
  if (ratio != null) params.sl_ratio = ratio;
  if (symbols?.length) params.symbols = symbols.join(',');
  if (anchor) params.anchor = anchor;
  const { data } = await axios.get(`${API_BASE}/analytics/active`, {
    params,
    headers: authHeaders(),
  });
  return data;
}

/**
 * Terminal home rollup — open at the user's TP/SL within `days` (default 3).
 * Independent of the sector strip's return window (that uses /pulse/sectors).
 */
export async function fetchActiveHome({ days = 3, itemsLimit = 12, setupLimit = 4, index, tp, ratio } = {}) {
  const params = { days, items_limit: itemsLimit, setup_limit: setupLimit };
  if (index) params.index = index;
  if (tp != null) params.tp_level = tp;
  if (ratio != null) params.sl_ratio = ratio;
  const { data } = await axios.get(`${API_BASE}/analytics/active/home`, {
    params,
    headers: authHeaders(),
  });
  return data;
}

/** The user's exit-grid preference. null fields = never chosen (gate stays). */
export async function fetchExitGrid() {
  const { data } = await axios.get(`${API_BASE}/mypicks/portfolio-settings`, {
    headers: authHeaders(),
  });
  return {
    tp: data?.exit_tp_pct ?? null,
    ratio: data?.exit_sl_ratio ?? null,
  };
}

/** Paged stock universe (sorted-stocks).
 *  `symbols` (array) scopes to an exact ticker list — use it, not `search`, when
 *  the set is known: `search` is a single ILIKE, so a comma-joined list matches
 *  the literal string and quietly returns nothing. An empty array is a real
 *  empty selection and is sent as such; omit the key entirely for "unscoped". */
export async function fetchSortedStocks({ page = 1, perPage = 60, search, index, sortBy = 'momentum', symbols } = {}) {
  const params = { page, per_page: perPage, sort_by: sortBy };
  if (search) params.search = search;
  if (index) params.index = index;
  if (Array.isArray(symbols)) params.symbols = symbols.join(',');
  const { data } = await axios.get(`${API_BASE}/stocks/sorted-stocks`, {
    params,
    headers: authHeaders(),
  });
  return data;
}

/** Per-setup leaderboard (win rate, behaviour, category, active counts).
 *  Pass `anchor` — a name or an array of them — when only some setups' rows
 *  are needed. Scoped calls read designer_edge_metrics_cache (fast) plus a
 *  small avg-days query; full board or cache miss falls back to live aggregate.
 *  Arrays serialise as repeated anchor[] keys (comma-joined strings do NOT work). */
export async function fetchSetupsLeaderboard({ days = 365, tp, ratio, anchor, index, windows } = {}) {
  const params = {};
  if (windows?.length) {
    params.windows = windows.join(',');
  } else {
    params.days = days;
  }
  if (tp != null) params.tp_level = tp;
  if (ratio != null) params.sl_ratio = ratio;
  if (index) params.index = index;
  if (Array.isArray(anchor)) {
    if (anchor.length) params.anchor = anchor;
  } else if (anchor) {
    params.anchor = anchor;
  }
  const { data } = await axios.get(`${API_BASE}/analytics/setups`, {
    params,
    headers: authHeaders(),
  });
  return data;
}

/** Map a /setups row to the cell shape ConfluenceFunnel + share models expect. */
export function setupLeaderboardCell(row) {
  if (!row) return null;
  const decided = (row.tp || 0) + (row.sl || 0);
  if (!decided) return null;
  return {
    decided,
    win_rate: row.win_rate,
    avg_days_to_outcome: row.avg_days_to_outcome,
    tp: row.tp,
    sl: row.sl,
    total: row.total,
  };
}

/**
 * TP/SL matrix — every setup × every stored combo in one call. Each cell
 * carries win_rate, decided and avg_days_to_outcome; the Playbook reads
 * "Hit TP" and "Avg holding time" from the cell matching the user's exit grid,
 * which is why holding time moves when the grid moves — a wider target
 * genuinely takes longer to reach.
 *
 * avg_days_to_outcome already excludes the legacy negative rows server-side
 * (they were computed off created_at, pending the A5 history regeneration).
 */
export async function fetchSetupMatrix({ days = 365 } = {}) {
  const { data } = await axios.get(`${API_BASE}/analytics/setups/matrix`, {
    params: { days },
    headers: authHeaders(),
  });
  return data;
}

/**
 * Parameterized sub-setups — which setups expose a setting, and which of those
 * settings actually have history yet.
 *
 * Each option carries its own `anchor` and `trigger_count`. The count is the
 * whole point: a variant that has never been generated reads 0, and the UI
 * must say "not measured yet" rather than falling back to the parent's record.
 * A setting the product hasn't backtested has no win rate, and showing the
 * default's number under it would be a fabricated claim.
 */
export async function fetchSubSetups({ days = 365 } = {}) {
  const { data } = await axios.get(`${API_BASE}/analytics/subsetups`, {
    params: { days },
    headers: authHeaders(),
  });
  if (!data?.success) throw new Error(data?.error || 'sub-setups unavailable');
  return data.subsetups || [];
}

/**
 * Combo funnel — setups in ORDER on the same stock. Real trigger dates: the
 * per-stage attrition is the product insight, so it must never be estimated
 * client-side. `stages` are anchor names; `within` has one entry per link.
 *
 * Scoped via `index` — Terminal passes the user's universe picker value.
 */
export const COMBO_FUNNEL_INDEX = 'fno';

export function comboFunnelWindowLabel(days, index) {
  const span = days === 365 ? 'In 1 year' : `In ${days} days`;
  return index ? `${span} · ${universeLabel(index)}` : span;
}

export async function fetchComboFunnel({ stages, within, tp, ratio, days = 365, index } = {}) {
  const params = new URLSearchParams();
  (stages || []).forEach((s) => params.append('stage', s));
  (within || []).forEach((w) => params.append('within', w));
  if (tp != null) params.append('tp_level', tp);
  if (ratio != null) params.append('sl_ratio', ratio);
  params.append('days', days);
  if (index) params.append('index', index);
  const { data } = await axios.get(`${API_BASE}/analytics/setups/combo?${params.toString()}`, {
    headers: authHeaders(),
  });
  return data;
}

export async function saveExitGrid(tp, ratio) {
  const { data } = await axios.put(
    `${API_BASE}/mypicks/portfolio-settings`,
    { exit_tp_pct: tp, exit_sl_ratio: ratio },
    { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
  );
  return { tp: data?.exit_tp_pct ?? null, ratio: data?.exit_sl_ratio ?? null };
}

/** Per-trigger rows across stocks for one setup — the cross-stock ledger the
 *  setup page shows. /active only knows what is OPEN, and signal-overlay is
 *  per-symbol, so neither can answer "every trigger this setup ever fired". */
export async function fetchSetupTriggers({ anchor, days = 365, tp, ratio, limit = 10, offset = 0, index, skipTotal } = {}) {
  const params = { days, limit, offset };
  if (anchor) params.anchor = anchor;
  if (index) params.index = index;
  if (tp != null) params.tp_level = tp;
  if (ratio != null) params.sl_ratio = ratio;
  if (skipTotal) params.skip_total = 1;
  const { data } = await axios.get(`${API_BASE}/analytics/triggers`, {
    params,
    headers: authHeaders(),
  });
  return data;
}
